import Button from "@/components/Button"
import DevErrorToast from "@/components/DevErrorToast"
import Dialog from "@/components/Dialog"
import { CheckboxInput, DateInput } from "@/components/InputFields"
import InputLabel from "@/components/InputLabel"
import Spinner from "@/components/Spinner"
import Toast from "@/components/Toast"
import { useUpdatePregnancyMutation } from "@/data/queries"
import { Pregnancy } from "@/model/Pregnancy"
import { LocalDate } from "@js-joda/core"
import { useEffect } from "react"
import { useForm } from "react-hook-form"
import { toast } from "sonner"

type EditPregnancyModalProps = {
  isOpen: boolean
  onClose: () => void
  bloomlifeId: string
  pregnancy: Pregnancy
}

type EditPregnancyFormData = {
  estimatedDateOfDelivery?: string
  deliveryDate?: string
  patientOptedInClinically: boolean
}

const EditPregnancyModal = ({
  isOpen,
  onClose,
  bloomlifeId,
  pregnancy,
}: EditPregnancyModalProps) => {
  const valuesFromPregnancy = (p: Pregnancy): EditPregnancyFormData => ({
    estimatedDateOfDelivery: p.estimatedDateOfDelivery?.toString() ?? "",
    deliveryDate: p.deliveryDate?.toString() ?? "",
    patientOptedInClinically: p.patientOptedInClinically ?? false,
  })

  const { register, handleSubmit, reset, formState } =
    useForm<EditPregnancyFormData>({
      mode: "onChange",
      defaultValues: valuesFromPregnancy(pregnancy),
    })

  useEffect(() => {
    if (isOpen) {
      reset(valuesFromPregnancy(pregnancy))
    }
    // resetting on every render would clobber the user's typing mid-edit
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  const { mutateAsync: updatePregnancy, isPending } = useUpdatePregnancyMutation(
    { bloomlifeId }
  )

  const handleClose = () => {
    onClose()
  }

  const onSubmit = async (formData: EditPregnancyFormData) => {
    try {
      await updatePregnancy({
        bloomlifeId,
        pregnancyId: pregnancy.id,
        update: {
          estimatedDateOfDelivery: formData.estimatedDateOfDelivery || null,
          deliveryDate: formData.deliveryDate || null,
          patientOptedInClinically: formData.patientOptedInClinically,
        },
      })
      handleClose()
      toast.custom((toastId) => (
        <Toast toastId={toastId} description="Pregnancy updated successfully." />
      ))
    } catch (error) {
      toast.custom((toastId) => (
        <DevErrorToast
          toastId={toastId}
          userMessage="Something went wrong while updating the pregnancy. Try again, or contact the dev team."
          error={error}
        />
      ))
    }
  }

  return (
    <Dialog title="Edit pregnancy" isOpen={isOpen} onClose={handleClose}>
      <form
        className="flex flex-col gap-4"
        onSubmit={handleSubmit(onSubmit)}
        autoComplete="off"
      >
        <div className="grid grid-cols-2 gap-8">
          <div className="flex flex-col gap-1">
            <InputLabel htmlFor="pregnancy-edd" className="ml-1">
              Estimated delivery date
            </InputLabel>
            <DateInput
              id="pregnancy-edd"
              {...register("estimatedDateOfDelivery")}
            />
          </div>

          <div className="flex flex-col gap-1">
            <InputLabel htmlFor="pregnancy-delivery-date" className="ml-1">
              Delivery date
            </InputLabel>
            <DateInput
              id="pregnancy-delivery-date"
              max={LocalDate.now().toString()}
              {...register("deliveryDate")}
            />
          </div>
        </div>

        <div className="flex flex-row items-center gap-2">
          <CheckboxInput
            id="pregnancy-opted-in"
            {...register("patientOptedInClinically")}
          />
          <InputLabel htmlFor="pregnancy-opted-in" className="ml-1">
            Patient opted in clinically
          </InputLabel>
        </div>

        <div className="mt-2 flex justify-end gap-3">
          <Button
            type="button"
            variant="neutral"
            onClick={handleClose}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            className="flex items-center gap-3 px-6"
            disabled={!formState.isDirty || isPending}
          >
            {isPending && <Spinner className="size-4" />}
            Save
          </Button>
        </div>
      </form>
    </Dialog>
  )
}

export default EditPregnancyModal
